import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { CircleOutline, RadioOutlineLegacy } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Stack, Typography } from '@lyearn/ui';
import { DatabaseRatingTypeEnum } from '@/types/schema';

import { RatingStyleFormFieldProps, RatingStyleItemProps } from './types';
import { getRatingStyleItems } from './utils';

function RatingStyleItem(props: RatingStyleItemProps) {
  const { item, isSelected, onChange } = props;
  const { Icon, label, value } = item;
  const icon = isSelected ? (
    <RadioOutlineLegacy className="h-16 w-16 ms-auto" fill="none" />
  ) : (
    <CircleOutline className="h-16 w-16 ms-auto" fillColorToken="border" />
  );

  const onClick = useCallback(() => onChange(value), [onChange, value]);

  return (
    <Stack
      alignItems="center"
      className={cn(
        'flex-1 rounded-8 border border-solid py-8 px-12',
        isSelected ? 'border-focus-primary' : 'border-field',
      )}
      role="button"
      onClick={onClick}>
      <Icon className="h-16 w-16" fillColorToken={isSelected ? 'icon-active' : 'icon-primary'} />
      <Typography className="ms-8" color="text-text-primary" variant="body-short-02">
        {label}
      </Typography>
      {icon}
    </Stack>
  );
}

function RatingStyleFormField(props: RatingStyleFormFieldProps) {
  const { value = DatabaseRatingTypeEnum.Number, onChange } = props;
  const { t } = useTranslation();
  const items = useMemo(() => getRatingStyleItems(t), [t]);

  return (
    <Stack gapX={8}>
      {items.map((item) => (
        <RatingStyleItem
          key={item.value}
          isSelected={item.value === value}
          item={item}
          onChange={onChange}
        />
      ))}
    </Stack>
  );
}

export default RatingStyleFormField;
